import { BreakpointObserver } from '@angular/cdk/layout';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input, OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { from, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    getAdjacentSteps,
    updatePropertySteps
} from '../../helpers/property-step.helper';
import { StepIds } from '../../models/property-step/property-step-model';
import { PROPERTY_UI_COMPACT_MAX_WIDTH } from '../../property-ui-constants';
import {
    DraftFacade,
    IRouterData,
    RouterFacade
} from '@classifieds-sell/state';
import { AppStepIndicatorViewModel } from '@classifieds-sell/shared';

@Component({
    selector: 'property-tabs',
    templateUrl: './property-tabs.component.html',
    styleUrls: ['./property-tabs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PropertyTabsComponent implements OnInit {
    private _destroyed$ = new Subject<void>();
    private _routerData: IRouterData;

    public isCompact: boolean;

    @Input() selectedStepId: StepIds;
    @Output() beforeStepChange = new EventEmitter<{
        cb: (arg?: unknown) => void;
        arg?: unknown;
        isBack?: boolean;
    }>();

    public stepIndicatorViewModel: AppStepIndicatorViewModel;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _breakpointObserver: BreakpointObserver,
        private _routerFacade: RouterFacade,
        private _draftFacade: DraftFacade,
        private _router: Router,
        private _activatedRoute: ActivatedRoute
    ) {}

    ngOnInit(): void {
        from(this._routerFacade.routerData$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((stateData) => {
                this._routerData = stateData;
                this.updateStepIndicator();
            });

        // Page validation results to determine if any steps need to be enabled
        from(this._draftFacade.validationResults$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((validationResults) => {
                if (validationResults && validationResults.length > 0) {
                    const propertySteps = updatePropertySteps(
                        this._routerData.steps,
                        validationResults
                    );
                    this._routerFacade.updateSteps(propertySteps);
                }
            });

        this._breakpointObserver
            .observe([PROPERTY_UI_COMPACT_MAX_WIDTH])
            .subscribe((result) => {
                this.isSmallChange(result.matches);
            });
    }

    public onBeforeStepChange(
        cb: (arg?: unknown) => void,
        arg?: unknown,
        isBack?: boolean
    ) {
        this.beforeStepChange.emit({ cb: cb, arg: arg, isBack: isBack });
    }

    public onSelectedStepChange(stepId: StepIds) {
        this._router.navigate([stepId], {
            relativeTo: this._activatedRoute,
            queryParams: this._activatedRoute.snapshot.queryParams
        });
    }

    private isSmallChange(isSmall: boolean) {
        this.isCompact = isSmall;
        this._changeDetectorRef.markForCheck();
    }

    private updateStepIndicator(): void {
        this.stepIndicatorViewModel = new AppStepIndicatorViewModel(
            [
                this._routerData.categoryId.toString(),
                this._routerData.subCategory
            ],
            this._routerData.steps,
            this._routerData.stepInfo?.stepId
        );
        this.selectedStepId = this.stepIndicatorViewModel
            ?.selectedStepId as StepIds;

        const adjacentSteps = getAdjacentSteps(
            this._routerData.steps,
            this.selectedStepId
        );
        if (
            (this._routerData.stepInfo?.stepId || '') !== this.selectedStepId ||
            (this._routerData.stepInfo?.nextStepId || '') !==
                (adjacentSteps[1] || '') ||
            (this._routerData.stepInfo?.prevStepId || '') !==
                (adjacentSteps[0] || '')
        ) {
            this.updateStepInfo(
                this.selectedStepId,
                adjacentSteps[1],
                adjacentSteps[0]
            );
        }

        this._changeDetectorRef.markForCheck();
    }

    private updateStepInfo(
        currentStepId: string,
        nextStepId: string,
        prevStepId: string
    ): void {
        const currentStep = this.stepIndicatorViewModel.steps.find(
            (s) => s.stepId === currentStepId
        );

        const nextStep = this.stepIndicatorViewModel.steps.find(
            (s) => s.stepId === nextStepId
        );

        const prevStep = this.stepIndicatorViewModel.steps.find(
            (s) => s.stepId === prevStepId
        );

        const stepInfo = {
            stepId: currentStep?.stepId,
            step: currentStep,
            nextStepId: nextStep?.stepId,
            nextStep: nextStep,
            prevStepId: prevStep?.stepId,
            prevStep: prevStep
        };

        this._routerFacade.setRouterStepInfo(stepInfo);
    }
}
